import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { List, BarChart2, RefreshCw } from 'lucide-react';
import { exportToCsv, printReport, ExportPrintBar } from '../../utils/reportExport';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN');
}

const SHIPMENT_TYPES = ['AIR', 'SEA', 'ROAD', 'RAIL', 'COURIER'];
const DOCKET_STATUSES = ['INITIATE', 'PICK_UP', 'IN_TRANSIT', 'DELIVERED', 'COMPLETED', 'CANCELLED'];

const TYPE_COLORS = {
  AIR: 'bg-sky-100 text-sky-700',
  SEA: 'bg-blue-100 text-blue-700',
  ROAD: 'bg-amber-100 text-amber-700',
  RAIL: 'bg-purple-100 text-purple-700',
  COURIER: 'bg-green-100 text-green-700',
};
const STATUS_COLORS = {
  INITIATE: 'bg-gray-100 text-gray-600',
  PICK_UP: 'bg-yellow-100 text-yellow-700',
  IN_TRANSIT: 'bg-blue-100 text-blue-700',
  DELIVERED: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-green-600 text-white',
  CANCELLED: 'bg-red-100 text-red-700',
};

// ─── Filter Bar ────────────────────────────────────────────────
function FilterBar({ shipmentType, setShipmentType, status, setStatus, dateFrom, setDateFrom, dateTo, setDateTo, onApply }) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Type</label>
          <Select value={shipmentType || '__all__'} onValueChange={v => setShipmentType(v === '__all__' ? '' : v)}>
            <SelectTrigger className="font-ibm h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Types</SelectItem>
              {SHIPMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Status</label>
          <Select value={status || '__all__'} onValueChange={v => setStatus(v === '__all__' ? '' : v)}>
            <SelectTrigger className="font-ibm h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {DOCKET_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="font-ibm h-9 w-40" />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="font-ibm h-9 w-40" />
        </div>
        <Button size="sm" onClick={onApply} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
          <RefreshCw className="w-3 h-3 mr-1" />Apply
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setShipmentType(''); setStatus(''); setDateFrom(''); setDateTo(''); onApply(); }} className="h-9">
          Clear
        </Button>
      </div>
    </Card>
  );
}

// ─── Tab 1: Docket Summary List ────────────────────────────────
function DocketSummaryList() {
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipmentType, setShipmentType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (shipmentType) p.append('shipment_type', shipmentType);
    if (status) p.append('status', status);
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    fetch(`${API}/api/reports/dockets/summary?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setDockets(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [shipmentType, status, dateFrom, dateTo]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const total = dockets.length;
  const byType = dockets.reduce((acc, d) => { acc[d.shipment_type] = (acc[d.shipment_type] || 0) + 1; return acc; }, {});

  const DOCKET_COLS = [
    { header: 'Docket No.', key: 'system_docket_no' },
    { header: 'Printed No.', key: 'printed_docket_no' },
    { header: 'Company', key: 'company_name' },
    { header: 'Type', key: 'shipment_type' },
    { header: 'Status', key: 'status' },
    { header: 'Exporter', key: 'exporter_name' },
    { header: 'Importer', key: 'importer_name' },
    { header: 'Created', key: 'created_at', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
    { header: 'Job Date', key: 'job_date', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
  ];
  const handleExport = () => exportToCsv('Docket_Summary', DOCKET_COLS, dockets);
  const handlePrint = () => printReport('Docket Summary Report', DOCKET_COLS, dockets);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {SHIPMENT_TYPES.map(t => (
          <Card key={t} className="p-3 text-center cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setShipmentType(t); fetch_(); }}>
            <p className="text-xl font-bold font-manrope text-text-dark">{byType[t] || 0}</p>
            <Badge className={`mt-1 text-xs ${TYPE_COLORS[t] || 'bg-gray-100 text-gray-600'}`}>{t}</Badge>
          </Card>
        ))}
      </div>

      <FilterBar shipmentType={shipmentType} setShipmentType={setShipmentType} status={status} setStatus={setStatus} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} onApply={fetch_} />

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="text-sm font-semibold text-text-dark">
            {total} Docket{total !== 1 ? 's' : ''}
          </span>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || dockets.length === 0} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Docket No.', 'Printed No.', 'Company', 'Type', 'Status', 'Exporter', 'Importer', 'Created', 'Job Date'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : dockets.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-text-secondary">No dockets found for the selected filters</td></tr>
              ) : dockets.map(d => (
                <tr key={d.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-text-dark font-ibm whitespace-nowrap">{d.system_docket_no}</td>
                  <td className="px-4 py-3 text-text-secondary font-ibm">{d.printed_docket_no || '-'}</td>
                  <td className="px-4 py-3 text-text-dark text-xs">{d.company_name || '-'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${TYPE_COLORS[d.shipment_type] || 'bg-gray-100 text-gray-600'}`}>{d.shipment_type}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>{d.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-text-dark text-xs max-w-[120px] truncate">{d.exporter_name || '-'}</td>
                  <td className="px-4 py-3 text-text-dark text-xs max-w-[120px] truncate">{d.importer_name || '-'}</td>
                  <td className="px-4 py-3 text-text-secondary text-xs whitespace-nowrap">{formatDate(d.created_at)}</td>
                  <td className="px-4 py-3 text-text-secondary text-xs whitespace-nowrap">{d.job_date ? formatDate(d.job_date) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab 2: Shipment Stats ─────────────────────────────────────
function ShipmentStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    fetch(`${API}/api/reports/dockets/stats?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const STATS_COLS_TYPE = [{ header: 'Shipment Type', key: 'label' }, { header: 'Count', key: 'count' }];
  const STATS_COLS_STATUS = [{ header: 'Status', key: 'label' }, { header: 'Count', key: 'count' }];
  const handleExport = () => {
    if (!stats) return;
    exportToCsv('Docket_Type_Stats', STATS_COLS_TYPE, stats.by_shipment_type);
    exportToCsv('Docket_Status_Stats', STATS_COLS_STATUS, stats.by_status);
  };
  const handlePrint = () => {
    if (!stats) return;
    printReport('Docket Shipment Analytics',
      [{ header: 'Category', key: 'label' }, { header: 'Sub-Label', key: 'sub' }, { header: 'Count', key: 'count' }],
      [
        ...stats.by_shipment_type.map(x => ({ label: 'By Type', sub: x.label, count: x.count })),
        ...stats.by_status.map(x => ({ label: 'By Status', sub: x.label, count: x.count })),
      ]
    );
  };

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || !stats} />
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-text-secondary">Loading stats...</Card>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* By Shipment Type */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-text-dark mb-4 font-manrope uppercase tracking-wider">By Shipment Type</h3>
            <div className="space-y-3">
              {stats.by_shipment_type.map(item => {
                const pct = stats.total > 0 ? Math.round((item.count / stats.total) * 100) : 0;
                return (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`text-xs ${TYPE_COLORS[item.label] || 'bg-gray-100 text-gray-600'}`}>{item.label}</Badge>
                      <span className="text-sm font-bold font-ibm text-text-dark">{item.count} <span className="font-normal text-text-secondary">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-accent-blue rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {stats.by_shipment_type.length === 0 && <p className="text-sm text-text-secondary">No data</p>}
            </div>
          </Card>

          {/* By Status */}
          <Card className="p-5">
            <h3 className="text-sm font-bold text-text-dark mb-4 font-manrope uppercase tracking-wider">By Status</h3>
            <div className="space-y-3">
              {stats.by_status.map(item => {
                const pct = stats.total > 0 ? Math.round((item.count / stats.total) * 100) : 0;
                return (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`text-xs ${STATUS_COLORS[item.label] || 'bg-gray-100 text-gray-600'}`}>{item.label}</Badge>
                      <span className="text-sm font-bold font-ibm text-text-dark">{item.count} <span className="font-normal text-text-secondary">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-accent-blue rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {stats.by_status.length === 0 && <p className="text-sm text-text-secondary">No data</p>}
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────
const TABS = [
  { id: 'list', label: 'Docket Summary', icon: List },
  { id: 'stats', label: 'Shipment Analytics', icon: BarChart2 },
];

export default function DocketReports() {
  const [activeTab, setActiveTab] = useState('list');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-dark font-manrope">Docket Reports</h1>
        <p className="text-sm text-text-secondary mt-1">Shipment summary, type breakdown and status analysis</p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-docket-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-ibm border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-text-secondary hover:text-text-dark'
              }`}
            >
              <Icon className="w-4 h-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'list' && <DocketSummaryList />}
      {activeTab === 'stats' && <ShipmentStats />}
    </div>
  );
}
